// @oasis-permission-inherits: /users (users:view)
import { Icon } from "@mirage-web/icons";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Skeleton } from "~/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { useAuthStore } from "~/modules/auth/auth-store";
import { CreditDebitDialog } from "~/modules/credits/components/credit-debit-dialog";
import { CreditGrantDialog } from "~/modules/credits/components/credit-grant-dialog";
import { CustomCaptionTemplatesSection } from "~/modules/custom-caption-templates/components";
import { formatStore, getOrgRevenueCatAppUserId } from "~/modules/lookup/billing-links";
import { BillingLinks } from "~/modules/lookup/components/billing";
import { UserInfo } from "~/modules/lookup/components/user-info";
import { useAutoSaveSearch } from "~/modules/lookup/hooks/use-saved-searches";
import { removeLinkedEmailMutation } from "~/modules/lookup/mutations";
import { lookupUserBillingQuery, lookupUserByIdQuery } from "~/modules/lookup/queries";
import { orgByDefaultStateQuery } from "~/modules/org-by-default/queries";

const querySchema = z.object({
  sheet: z.enum(["grant", "debit"]).optional(),
});

interface CreditActionMenuItemProps {
  userId: string;
  sheet: "grant" | "debit";
  label: string;
  disabledReason?: string;
}

function getCreditActionDisabledReason(
  action: "grant" | "debit",
  isDeleted: boolean,
  hasPermission: boolean
): string | undefined {
  if (isDeleted) {
    return `Credits cannot be ${action === "grant" ? "granted" : "debited"} for a deleted account`;
  }
  if (!hasPermission) {
    return `You don't have permission to ${action} credits`;
  }
  return;
}

function CreditActionMenuItem({ userId, sheet, label, disabledReason }: CreditActionMenuItemProps) {
  const disabled = !!disabledReason;
  const menuItem = (
    <DropdownMenuItem disabled={disabled}>
      <Icon name="person" className="h-4 w-4 mr-2" />
      {label}
    </DropdownMenuItem>
  );

  const content = disabled ? (
    <div className="pointer-events-auto">{menuItem}</div>
  ) : (
    <Link
      to="/users/view/$userId"
      params={{ userId }}
      search={{ sheet }}
      className="text-sm text-primary hover:underline"
    >
      {menuItem}
    </Link>
  );

  if (!disabledReason) {
    return content;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent>{disabledReason}</TooltipContent>
    </Tooltip>
  );
}

export const Route = createFileRoute("/_dash/users/view/$userId")({
  component: RouteComponent,
  async loader({ params, context }) {
    const { queryClient } = context;

    if (!params.userId) {
      throw new Error("User ID is required");
    }

    queryClient.prefetchQuery(
      lookupUserByIdQuery.queryOptions({
        pathParams: { userId: params.userId },
      })
    );
    queryClient.prefetchQuery(
      orgByDefaultStateQuery.queryOptions({
        pathParams: { userId: params.userId },
      })
    );
  },
  pendingComponent: PendingComponent,
  validateSearch: querySchema,
});

function PendingComponent() {
  return (
    <div className="space-y-6">
      <div>
        <Link to="/users" className="text-sm text-muted-foreground hover:underline">
          ← Back to Users
        </Link>
      </div>

      <div>
        <Skeleton className="h-7 w-32" />
      </div>

      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="space-y-1">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            <div>
              <Skeleton className="h-6 w-20" />
            </div>
            <div>
              <Skeleton className="h-6 w-48" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-3">
            <Skeleton className="h-5 w-32" />
            <div className="space-y-2">
              <div className="flex justify-between">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <Skeleton className="h-5 w-44" />
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="flex items-center justify-between rounded-md border p-4">
              <div className="space-y-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-4 w-16" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { userId } = Route.useParams();
  const { sheet } = Route.useSearch();
  const [user] = useAuthStore((s) => [s.user]);
  const hasGrantCreditsPermission = user?.permissions.includes("credits:grant") ?? false;
  const hasLinkedinSkillsManagePermission =
    user?.permissions.includes("linkedin-skills:manage") ?? false;
  const hasUsersEditPermission = user?.permissions.includes("users:edit") ?? false;
  const hasUsersLogoutPermission = user?.permissions.includes("users:logout") ?? false;
  const hasAiEditToolViewPermission = user?.permissions.includes("ai-edit-v2-tool:view") ?? false;
  const isSelf = user?.userId === userId;

  const [dangerZoneOpen, setDangerZoneOpen] = useState(false);
  const [showRemoveEmailConfirm, setShowRemoveEmailConfirm] = useState(false);

  useEffect(() => {
    setDangerZoneOpen(false);
    setShowRemoveEmailConfirm(false);
  }, [userId]);

  // Deliberately not prefetched in the loader: billing hits RevenueCat/Stripe
  // live, so it streams in after the user itself has rendered.
  const billingQuery = lookupUserBillingQuery.useQuery({ pathParams: { userId } });
  const { data } = lookupUserByIdQuery.useSuspenseQuery({
    pathParams: { userId },
  });
  const { data: orgByDefaultState } = orgByDefaultStateQuery.useSuspenseQuery({
    pathParams: { userId },
  });

  const { mutate: removeEmail, isPending: isRemoveEmailPending } =
    removeLinkedEmailMutation.useMutation({
      onSuccess() {
        setShowRemoveEmailConfirm(false);
        toast.success("Linked email removed");
      },
      onError() {
        setShowRemoveEmailConfirm(false);
        toast.error("Failed to remove linked email");
      },
    });

  const isNotPhoneAccount = !data?.user?.phoneNumber;
  let removeEmailDescription = "Removes the email address linked to this phone account.";
  if (isSelf) {
    removeEmailDescription = "You cannot remove your own linked email.";
  } else if (isNotPhoneAccount) {
    removeEmailDescription = "Emails can only be removed from phone-signup accounts.";
  }

  const searchData = useMemo(() => {
    return data?.user ? { name: data.user.name } : undefined;
  }, [data?.user]);

  useAutoSaveSearch(userId, searchData, "user");

  if (!data?.user) {
    return (
      <div className="space-y-6">
        <div>
          <Link to="/users" className="text-sm text-muted-foreground hover:underline">
            ← Back to Users
          </Link>
        </div>
        <div className="rounded-md border bg-muted/20 p-4">
          <h3 className="font-semibold">No User Found</h3>
          <p className="text-sm text-muted-foreground">No user found with ID: {userId}</p>
        </div>
      </div>
    );
  }

  const isDeleted = !!data.user.deletedAt;

  return (
    <div className="space-y-6">
      {sheet === "grant" && (
        <CreditGrantDialog
          onClose={() => navigate({ search: (old) => ({ ...old, sheet: undefined }) })}
          preset={{ type: "user", id: data.user.userId }}
        />
      )}
      {sheet === "debit" && (
        <CreditDebitDialog
          onClose={() => navigate({ search: (old) => ({ ...old, sheet: undefined }) })}
          preset={{ type: "user", id: data.user.userId }}
        />
      )}

      <div className="flex">
        <div className="flex flex-1">
          <Link to="/users" className="text-sm text-muted-foreground hover:underline">
            ← Back to Users
          </Link>
        </div>

        <div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <Icon name="options" className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <Link
                to="/projects"
                search={{ userId }}
                className="text-sm text-primary hover:underline"
              >
                <DropdownMenuItem>
                  <Icon name="script" className="h-4 w-4 mr-2" />
                  View all projects
                </DropdownMenuItem>
              </Link>
              <Link
                to="/charges/users/$userId"
                params={{ userId }}
                className="text-sm text-primary hover:underline"
              >
                <DropdownMenuItem disabled={!hasGrantCreditsPermission}>
                  <Icon name="person" className="h-4 w-4 mr-2" />
                  Charge History
                </DropdownMenuItem>
              </Link>
              <CreditActionMenuItem
                userId={userId}
                sheet="grant"
                label="Grant Credits"
                disabledReason={getCreditActionDisabledReason(
                  "grant",
                  isDeleted,
                  hasGrantCreditsPermission
                )}
              />
              <CreditActionMenuItem
                userId={userId}
                sheet="debit"
                label="Debit Credits"
                disabledReason={getCreditActionDisabledReason(
                  "debit",
                  isDeleted,
                  hasGrantCreditsPermission
                )}
              />
              <Link
                to="/linkedin-skills/users/$userId"
                params={{ userId }}
                className="text-sm text-primary hover:underline"
              >
                <DropdownMenuItem disabled={!hasLinkedinSkillsManagePermission}>
                  <Icon name="person" className="h-4 w-4 mr-2" />
                  LinkedIn Skills
                </DropdownMenuItem>
              </Link>
              {hasUsersLogoutPermission ? (
                <Link
                  to="/users/force-sign-out/$userId"
                  params={{ userId }}
                  className="text-sm text-destructive hover:underline"
                >
                  <DropdownMenuItem className="text-destructive focus:text-destructive">
                    <Icon name="block" className="h-4 w-4 mr-2" />
                    Force Sign Out
                  </DropdownMenuItem>
                </Link>
              ) : (
                <DropdownMenuItem disabled className="text-destructive focus:text-destructive">
                  <Icon name="block" className="h-4 w-4 mr-2" />
                  Force Sign Out
                </DropdownMenuItem>
              )}
              {data.user.deletedAt ? (
                <Link
                  to="/users/undelete-account/$userId"
                  params={{ userId }}
                  className="text-sm text-primary hover:underline"
                >
                  <DropdownMenuItem disabled={!hasUsersEditPermission}>
                    <Icon name="undo" className="h-4 w-4 mr-2" />
                    Undelete Account
                  </DropdownMenuItem>
                </Link>
              ) : (
                <Link
                  to="/users/delete-account/$userId"
                  params={{ userId }}
                  className="text-sm text-destructive hover:underline"
                >
                  <DropdownMenuItem
                    disabled={!hasUsersEditPermission}
                    className="text-destructive focus:text-destructive"
                  >
                    <Icon name="trash" className="h-4 w-4 mr-2" />
                    Delete Account
                  </DropdownMenuItem>
                </Link>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div>
        <h1 className="text-xl font-bold">User Details</h1>
      </div>

      <UserInfo
        user={{
          id: data.user.userId,
          email: data.user.email,
          name: data.user.name,
          createdAt: data.user.createdAt,
          lastLoginAt: data.user.lastLoginAt,
          lastLogin: data.user.lastLogin,
          hasWorkosAccount: data.user.hasWorkosAccount,
          deletedAt: data.user.deletedAt,
          deletedReason: data.user.deletedReason,
          internalAccountType: data.user.internalAccountType,
          phoneNumber: data.user.phoneNumber,
          isOrgByDefault: orgByDefaultState.isOrgByDefault,
        }}
        credits={data.user.balance}
        billing={{
          appUserId: billingQuery.data?.revenueCat.appUserId ?? userId,
          subscription: billingQuery.data?.revenueCat.subscription ?? null,
          isPending: billingQuery.isPending,
          isError: billingQuery.isError,
        }}
      />

      <div className="space-y-4">
        <h3 className="font-medium">Associated Organizations</h3>
        {data.orgs.length > 0 ? (
          <div className="space-y-3">
            {data.orgs.map((org) => (
              <div
                key={org.workosOrg.id}
                className="flex items-center justify-between rounded-md border p-4"
              >
                <div className="space-y-1">
                  <p className="font-medium">{org.orgDetails.name}</p>
                  <p className="text-sm text-muted-foreground">ID: {org.orgDetails.id}</p>
                  <p className="text-sm text-muted-foreground">
                    {org.orgDetails.skuba
                      ? `${org.orgDetails.skuba.plan} · ${formatStore(org.orgDetails.skuba.store)}`
                      : "No subscription"}
                  </p>
                  <BillingLinks
                    appUserId={getOrgRevenueCatAppUserId(org.orgDetails.id)}
                    store={org.orgDetails.skuba?.store}
                    stripeCustomerId={org.orgDetails.stripeCustomerId}
                    stripeKeyMode={org.orgDetails.stripeKeyMode}
                  />
                </div>
                <div className="flex items-center gap-2">
                  {org.environment && org.environment !== "prod" && (
                    <Badge
                      variant="outline"
                      className={cn(
                        "border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400"
                      )}
                    >
                      {org.environment === "oasis_staging" ? "Oasis Staging" : "Staging"}
                    </Badge>
                  )}
                  {org.orgMembers.find((m) => m.captionsUser?.userId === data.user!.userId) && (
                    <Badge>
                      {
                        org.orgMembers.find((m) => m.captionsUser?.userId === data.user!.userId)
                          ?.role.slug
                      }
                    </Badge>
                  )}
                  <Link
                    to="/organizations/view/$orgId"
                    params={{ orgId: org.orgDetails.id }}
                    className="text-sm text-primary hover:underline"
                  >
                    View Org
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">No associated organizations found</p>
        )}
      </div>

      {hasUsersEditPermission && (
        <>
          <Collapsible open={dangerZoneOpen} onOpenChange={setDangerZoneOpen}>
            <div className="rounded-md border border-red-200 overflow-hidden">
              <CollapsibleTrigger asChild>
                <button
                  className={cn(
                    "w-full flex items-center justify-between p-4 text-left hover:bg-red-50/50 transition-colors",
                    dangerZoneOpen && "bg-red-50/30"
                  )}
                >
                  <h3 className="font-medium text-red-600">Danger Zone</h3>
                  <Icon
                    name={dangerZoneOpen ? "chevron-up" : "chevron-down"}
                    className="h-4 w-4 text-red-400"
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="border-t border-red-200 bg-red-50/30 p-4 space-y-3">
                  {!data.user.hasWorkosAccount && data.user.email ? (
                    <div className="flex items-center justify-between rounded-md border border-red-100 bg-white p-4">
                      <div className="space-y-1">
                        <p className="font-medium text-sm">Remove linked email</p>
                        <p className="text-sm text-muted-foreground">{removeEmailDescription}</p>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={isSelf || isNotPhoneAccount || isRemoveEmailPending}
                        onClick={() => setShowRemoveEmailConfirm(true)}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No linked email to remove.</p>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Dialog open={showRemoveEmailConfirm} onOpenChange={setShowRemoveEmailConfirm}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Remove Linked Email</DialogTitle>
                <DialogDescription>
                  This will remove the email address linked to this phone account. The user will
                  need to re-link an email if they want one associated with their account.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowRemoveEmailConfirm(false)}
                  disabled={isRemoveEmailPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => removeEmail({ params: {}, pathParams: { userId } })}
                  disabled={isRemoveEmailPending}
                >
                  {isRemoveEmailPending ? "Removing..." : "Remove"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}

      {hasAiEditToolViewPermission && <CustomCaptionTemplatesSection userId={userId} />}
    </div>
  );
}
